/**
 * Il palco del televisore: la tela 1920×1080, rimpicciolita per stare nel riquadro vero.
 *
 * ## Perché serve
 *
 * Le schermate del televisore sono costruite su misure fisse. Su Tizen il browser riporta davvero
 * 1920 px CSS e tutto combacia; su **Android TV** lo stesso schermo riporta 960, perché la densità è
 * ×2 — e metà interfaccia finisce oltre il bordo destro. Nella schermata di attivazione quella metà
 * contiene il QR e il codice: un box Android TV **non si può attivare**.
 *
 * ## `transform: scale` e non `zoom`
 *
 * `zoom` sarebbe una riga sola, ma non è la stessa cosa dappertutto: il pavimento di questa app è
 * Tizen 5, cioè un motore del 2017, e quel che si comporta diversamente in un disegno a schermo
 * intero non lo si scopre in laboratorio. `transform` è la stessa trasformazione ovunque da dieci
 * anni.
 *
 * ## Si misura, non si calcola dalla densità
 *
 * Si potrebbe dividere per `devicePixelRatio` e risparmiare un ascoltatore. Sarebbe sbagliato due
 * volte: il riquadro cambia anche senza che la densità cambi — una finestra ridimensionata, un
 * televisore che cede spazio a un riquadro di sistema — e `devicePixelRatio` su alcuni box riporta
 * valori che non c'entrano con lo spazio disponibile. Qui interessa **quanto spazio c'è**, e
 * l'unico modo di saperlo è guardarlo.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { TV_DESIGN_HEIGHT, TV_DESIGN_WIDTH, tvScale } from '@player/domain';

export function TvStage({ children }: { readonly children: ReactNode }): ReactNode {
  const [scala, setScala] = useState(() => misura());

  useEffect(() => {
    const aggiorna = (): void => setScala(misura());

    /*
      Si misura **anche subito**, e non solo agli eventi.

      Fra il primo disegno e questo effetto il riquadro può già essere cambiato — succede
      all'apertura, mentre il motore assesta la pagina — e senza questa riga quel cambiamento non
      arriverebbe fino al prossimo evento, cioè potenzialmente mai.
    */
    aggiorna();

    window.addEventListener('resize', aggiorna);
    window.addEventListener('orientationchange', aggiorna);
    return () => {
      window.removeEventListener('resize', aggiorna);
      window.removeEventListener('orientationchange', aggiorna);
    };
  }, []);

  return (
    <div
      data-tv-stage="1"
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        /*
          Centrato nei due versi: su un riquadro che non è 16:9 la tela avanza spazio da una parte, e
          lasciarlo tutto in fondo o di lato farebbe sembrare l'interfaccia incollata a un bordo.
        */
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        data-tv-canvas="1"
        style={{
          flex: 'none',
          width: TV_DESIGN_WIDTH,
          height: TV_DESIGN_HEIGHT,
          transform: `scale(${scala})`,
          /*
            L'origine al centro, perché il riquadro esterno centra già: con `top left` la tela
            resterebbe ancorata all'angolo e lo spazio avanzato finirebbe tutto in basso a destra.
          */
          transformOrigin: 'center center',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function misura(): number {
  if (typeof window === 'undefined') return 1;
  return tvScale(window.innerWidth, window.innerHeight);
}
